import { KiteIcon } from '@kite/react-kite';
import React, { useEffect, useState } from 'react';
import { useLayoutContext } from '../../providers/LayoutProvider';
import logo from '../../assets/spectrum-reach-logo-dkbkg.svg';
import { graphql, useStaticQuery } from 'gatsby';
import { queryBuilder } from '../../shared/Utils';
import { SmartLink } from '../../components/SmartLink/SmartLink';
import { DriftChatButton } from '../../components/DriftChatButton/DriftChatButton';
import { ParentNodeProvider } from '../../providers/ParentNodeProvider';
import './Footer.scss';

const ignoredDynamicMenus = [`Contact`, `Bottom Links`];

export const Footer = () => {
    const { campaignTermsFilter } = useLayoutContext();
    const [menuItems, setMenuItems] = useState([]);
    const data = useStaticQuery(graphql`
        query {
            allMenuItems(filter: { menu_name: { eq: "footer" } }) {
                nodes {
                    childrenMenuItems {
                        id
                        weight
                    }
                    id
                    url
                    title
                    weight
                }
            }
        }
    `);
    const [queryString, setQueryString] = useState(``);
    const queryParametersToString = queryBuilder(campaignTermsFilter, false);

    if (!menuItems.length) {
        const items = data.allMenuItems.nodes
            .sort(
                (a, b) => Number.parseInt(a.weight) - Number.parseInt(b.weight)
            )
            .filter((item) => item.childrenMenuItems.length > 0);
        for (const item of items) {
            item.children = [];
            item.expanded = false;
            for (const childItem of item.childrenMenuItems.sort(
                (a, b) => Number.parseInt(a.weight) - Number.parseInt(b.weight)
            )) {
                const match = data.allMenuItems.nodes.find(
                    (dItem) => dItem.id === childItem.id
                );
                if (match) {
                    item.children.push(match);
                }
            }
        }
        setMenuItems(items);
    }

    useEffect(() => {
        setQueryString(
            `${!queryParametersToString ? `` : queryParametersToString}`
        );
    }, [queryParametersToString]);

    return (
        <ParentNodeProvider nodeName={undefined} nodeType="Footer">
            <footer className="site-footer dark">
                <div className="kite-container">
                    <section className="grid">
                        <nav>
                            <ul>
                                {menuItems
                                    .find((item) => item.title === `Contact`)
                                    ?.children.map((item, idx) => (
                                        <li key={idx}>
                                            {idx === 1 && (
                                                <KiteIcon icon="ki-phone-f" />
                                            )}
                                            {idx === 2 && (
                                                <KiteIcon icon="ki-edit-f" />
                                            )}
                                            <SmartLink
                                                href={item.url}
                                                aria-label={
                                                    item.title ===
                                                    `Spectrum Logo`
                                                        ? `Spectrum Reach logo`
                                                        : undefined
                                                }
                                            >
                                                {item.title ===
                                                `Spectrum Logo` ? (
                                                    <img
                                                        alt="Spectrum Reach Logo"
                                                        src={logo}
                                                    />
                                                ) : (
                                                    item.title
                                                )}
                                            </SmartLink>
                                        </li>
                                    ))}
                                <li>
                                    <KiteIcon icon="ki-chat-f" />
                                    <DriftChatButton />
                                </li>
                            </ul>
                        </nav>
                        {menuItems
                            .filter(
                                (item) =>
                                    !ignoredDynamicMenus.includes(item.title)
                            )
                            ?.map((item, idx) => (
                                <nav
                                    className="desktop-only"
                                    key={`footer-nav-${idx}`}
                                >
                                    <ul>
                                        <li className="title">
                                            <h3>{item.title}</h3>
                                        </li>
                                        {item.children &&
                                            item.children.map(
                                                (childItem, idx) => (
                                                    <li
                                                        key={`footer-nav-item-${idx}`}
                                                    >
                                                        <SmartLink
                                                            href={childItem.url}
                                                        >
                                                            {childItem.title}
                                                        </SmartLink>
                                                    </li>
                                                )
                                            )}
                                    </ul>
                                </nav>
                            ))}
                        <div>
                            <h3>Connect With Us</h3>
                            <iframe
                                src={`https://go2.spectrumreach.com/l/211312/2022-10-13/8bkfr8${queryString}`}
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                title="Contact us"
                            ></iframe>
                            <nav>
                                <ul className="social">
                                    <li>
                                        <SmartLink
                                            aria-label="Spectrum Reach's Facebook Page"
                                            href="https://www.facebook.com/SpectrumReach/?ref=br_rs"
                                        >
                                            <KiteIcon
                                                size="1.5rem"
                                                icon="ki-facebook"
                                            />
                                        </SmartLink>
                                    </li>
                                    <li>
                                        <a
                                            href="https://twitter.com/SpectrumReach"
                                            aria-label="Spectrum Reach's Twitter account"
                                        >
                                            <KiteIcon
                                                size="1.5rem"
                                                icon="ki-x-twitter"
                                            />
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://www.instagram.com/spectrumreach/"
                                            aria-label="Spectrum Reach's Instagram account"
                                        >
                                            <KiteIcon
                                                size="1.5rem"
                                                icon="ki-instagram"
                                            />
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://www.linkedin.com/company/spectrum-reach/"
                                            aria-label="Spectrum Reach's LinkedIn Page"
                                        >
                                            <KiteIcon
                                                size="1.5rem"
                                                icon="ki-linkedin"
                                            />
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                        <div className="accordions">
                            {menuItems
                                .filter(
                                    (item) =>
                                        !ignoredDynamicMenus.includes(
                                            item.title
                                        )
                                )
                                .map((item, idx) => (
                                    <nav
                                        className="mobile-only accordion"
                                        key={`footer-accordion-${idx}`}
                                    >
                                        <button
                                            aria-expanded={item.expanded}
                                            aria-controls={`footer-menu-${idx}`}
                                            onClick={() => {
                                                item.expanded = !item.expanded;
                                                setMenuItems([...menuItems]);
                                            }}
                                        >
                                            <h3>{item.title}</h3>
                                            {!item.expanded && (
                                                <KiteIcon icon="ki-chevron-down" />
                                            )}
                                            {item.expanded && (
                                                <KiteIcon icon="ki-chevron-up" />
                                            )}
                                        </button>
                                        <ul
                                            id={`footer-menu-${idx}`}
                                            className={
                                                item.expanded
                                                    ? `expanded`
                                                    : undefined
                                            }
                                        >
                                            {item.children &&
                                                item.children.map(
                                                    (childItem, idx) => (
                                                        <li
                                                            key={`footer-menu-item-${idx}`}
                                                        >
                                                            <SmartLink
                                                                href={
                                                                    childItem.url
                                                                }
                                                            >
                                                                {
                                                                    childItem.title
                                                                }
                                                            </SmartLink>
                                                        </li>
                                                    )
                                                )}
                                        </ul>
                                    </nav>
                                ))}
                        </div>
                    </section>
                    <section className="bottom">
                        <nav>
                            {menuItems
                                .find((item) => item.title === `Bottom Links`)
                                ?.children.map((childItem, key) => (
                                    <SmartLink key={key} href={childItem.url}>
                                        {childItem.title}
                                    </SmartLink>
                                ))}
                        </nav>
                        <p>
                            If you are a customer with a disability, please{` `}
                            <SmartLink href="/contact">contact us</SmartLink> if
                            you need assistance.
                        </p>
                        <p>
                            ©{new Date().getFullYear()} Charter Communications.
                            All rights reserved.
                        </p>
                    </section>
                </div>
            </footer>
        </ParentNodeProvider>
    );
};
